// src/components/Properties/Properties.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Slider from "react-slick";
import axios from "axios";
import "./Properties.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = "/api";
// OR try without port if your backend serves both API and frontend:
// const API_BASE_URL = window.location.origin; // This will use the same origin as frontend

// ✅ FIX: Enhanced axios instance with CORS handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // Add withCredentials if needed for cookies/auth
  // withCredentials: true,
});

// ✅ FIX: Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 Making ${config.method?.toUpperCase()} request to:`,
      config.url
    );
    console.log("Full config:", config);
    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// ✅ FIX: Add response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

const Properties = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [category, setCategory] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Extract category from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryFromUrl = searchParams.get("category");

    if (categoryFromUrl) {
      console.log(`📌 Category from URL: ${categoryFromUrl}`);
      setCategory(categoryFromUrl);
    } else {
      setCategory("");
    }
  }, [location.search]);

  // Fetch properties based on category
  useEffect(() => {
    fetchPropertiesByCategory();
    // Reset to page 1 when category changes
    setCurrentPage(1);
  }, [category]);

  const fetchPropertiesByCategory = async () => {
    try {
      setLoading(true);
      console.log(`🔄 Fetching properties for category: ${category || "All"}`);

      let apiUrl = "/Homeproperties"; // ✅ CHANGED: Use relative URL
      let isFiltered = false;

      if (category) {
        apiUrl = `/properties?propertyType=${encodeURIComponent(category)}`; // ✅ CHANGED: Use relative URL
        isFiltered = true;
        console.log(`🔍 Using filtered API: ${apiUrl}`);
      }

      const res = await api.get(apiUrl);
      console.log("📦 API response:", res.data);

      let data = [];
      if (res.data.data) {
        data = res.data.data;
      } else if (res.data.properties) {
        data = res.data.properties;
      } else if (Array.isArray(res.data)) {
        data = res.data;
      }

      console.log(
        `🏠 Found ${data.length} properties for category: ${category || "All"}`
      );

      const parsedData = data.map((property) => {
        let parsedImages = [];

        const possibleImageFields = [
          "images",
          "image",
          "photos",
          "imageUrl",
          "displayImage",
        ];

        let imagesData = null;
        for (const field of possibleImageFields) {
          if (property[field]) {
            console.log(
              `📸 Found images in field '${field}' for property ${property.id}:`,
              property[field]
            );
            imagesData = property[field];
            break;
          }
        }

        if (!imagesData) {
          console.log(`⚠️ No image data found for property ${property.id}`);
        }

        // Parse images data
        if (imagesData) {
          try {
            if (typeof imagesData === "string") {
              if (
                imagesData.trim().startsWith("[") ||
                imagesData.trim().startsWith("{")
              ) {
                const parsed = JSON.parse(imagesData);
                if (Array.isArray(parsed)) {
                  parsedImages = parsed;
                } else if (parsed && typeof parsed === "object") {
                  parsedImages = [parsed];
                }
              } else {
                parsedImages = [{ FilePath: imagesData }];
              }
            } else if (Array.isArray(imagesData)) {
              parsedImages = imagesData;
            } else if (typeof imagesData === "object") {
              parsedImages = [imagesData];
            }
          } catch (error) {
            console.error(
              `❌ Error parsing images for property ${property.id}:`,
              error
            );
            if (typeof imagesData === "string") {
              parsedImages = [{ FilePath: imagesData }];
            }
          }
        }

        return {
          ...property,
          parsedImages,
        };
      });

      if (isFiltered) {
        setProperties(parsedData);
      } else {
        setProperties(parsedData);
      }

      if (category && parsedData.length === 0) {
        console.log(`⚠️ No properties found for category: ${category}`);
      }
    } catch (err) {
      console.error("❌ Error loading properties:", err);
    } finally {
      setLoading(false);
    }
  };

  // const getImageUrl = useCallback((img) => {
  //   if (!img) return "/placeholder.jpg";

  //   const imgPath =
  //     img.FilePath ||
  //     img.filePath ||
  //     img.url ||
  //     img.imageUrl ||
  //     img.src ||
  //     img.image ||
  //     img;

  //   if (!imgPath) return "/placeholder.jpg";

  //   // Already full URL
  //   if (imgPath.startsWith("http")) {
  //     return imgPath;
  //   }

  //   // ✅ FIX: Always prepend backend URL
  //   if (imgPath.startsWith("/")) {
  //     return `${imgPath}`;
  //   }

  //   return `${imgPath}`;
  // }, []);

  const getImageUrl = useCallback((img) => {
    if (!img) return "/placeholder.jpg";

    const imgPath =
      img.FilePath ||
      img.filePath ||
      img.url ||
      img.imageUrl ||
      img.src ||
      img.image ||
      img;

    if (!imgPath) return "/placeholder.jpg";

    // ✅ If already full URL
    if (imgPath.startsWith("http")) {
      return imgPath;
    }

    // ✅ FORCE absolute root path (CRITICAL FIX)
    if (imgPath.startsWith("/uploads/")) {
      return imgPath;
    }

    // ✅ Safety fallback
    return `/uploads/${imgPath.replace(/^\/+/, "")}`;
  }, []);

  // Handle image loading errors
  const handleImageError = (imageUrl, propertyId, imageIndex) => {
    console.error(`❌ Image failed to load for property ${propertyId}:`, {
      imageUrl,
      imageIndex,
    });

    setImageErrors((prev) => ({
      ...prev,
      [`${propertyId}-${imageIndex}`]: true,
    }));
  };

  const handleImageLoad = (imageUrl, propertyId, imageIndex) => {
    console.log(
      `✅ Image loaded successfully for property ${propertyId}: ${imageUrl}`
    );
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    pauseOnHover: true,
    adaptiveHeight: true,
    focusOnSelect: false,
  };

  const handleViewDetails = (id) => {
    navigate(`/property/${id}`);
  };

  // Helper functions for dynamic titles
  const getSectionTitle = () => {
    if (category) {
      return `${category} Properties`;
    }
    return "Featured Properties";
  };

  const getSectionSubtitle = () => {
    if (category) {
      return `Browse all our ${category.toLowerCase()} properties`;
    }
    return "Discover our handpicked selection of premium properties";
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProperties = properties.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(properties.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <p className="text-center">Loading Properties...</p>;

  return (
    <section className="properties">
      <div className="container">
        <div className="section-titl">
          <h2 style={{ color: "black" }}>{getSectionTitle()}</h2>
          <p>{getSectionSubtitle()}</p>

          {category && (
            <div className="category-filter-badge">
              <span>
                Filtering by: <strong>{category}</strong>
              </span>
              <button
                className="clear-filter-btn"
                onClick={() => {
                  navigate("/properties");
                  setCurrentPage(1);
                }}
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>

        {category && properties.length === 0 && !loading && (
          <div className="no-properties-message">
            <p>No properties found for "{category}".</p>
            <button
              className="view-all-btn"
              onClick={() => {
                navigate("/properties");
                setCurrentPage(1);
              }}
            >
              View All Properties
            </button>
          </div>
        )}

        {/* Properties Grid */}
        <div className="properties-grid">
          {currentProperties.map((property) => {
            const hasValidImages =
              property.parsedImages && property.parsedImages.length > 0;

            return (
              <div key={property.id} className="property-card">
                <div className="property-image">
                  {hasValidImages ? (
                    <div className="slider-container">
                      <Slider {...sliderSettings}>
                        {property.parsedImages.map((img, index) => {
                          const imageUrl = getImageUrl(img);
                          const hasError =
                            imageErrors[`${property.id}-${index}`];

                          return (
                            <div key={index} className="slide-wrapper">
                              {hasError ? (
                                <div className="image-error">
                                  <div className="error-content">
                                    <span className="error-icon">📷</span>
                                    <span>Image not available</span>
                                    <small>Check image path</small>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={imageUrl}
                                  alt={`${
                                    property?.name || "Property"
                                  } - Image ${index + 1}`}
                                  className="property-main-image"
                                  onError={() =>
                                    handleImageError(
                                      imageUrl,
                                      property.id,
                                      index
                                    )
                                  }
                                  onLoad={() =>
                                    handleImageLoad(
                                      imageUrl,
                                      property.id,
                                      index
                                    )
                                  }
                                  loading="lazy"
                                  crossOrigin="anonymous"
                                />
                              )}
                            </div>
                          );
                        })}
                      </Slider>
                    </div>
                  ) : (
                    <div className="image-placeholder">
                      <div className="placeholder-content">
                        <span className="placeholder-icon">🏠</span>
                        <span>No Images Available</span>
                      </div>
                    </div>
                  )}

                  <div className="property-type-badge">
                    {property.propertyType || "Property"}
                  </div>
                </div>

                <div className="property-card-body">
                  <h3 className="property-title">
                    {property.name || property.title || "Property"}
                  </h3>
                  <p className="property-location">
                    {property.location ||
                      property.address ||
                      "Location unavailable"}
                  </p>

                  <div className="property-meta">
                    <span>{property.beds || "-"} Beds</span>
                    <span>{property.baths || "-"} Baths</span>
                    <span>{property.size || property.area || "-"} sqft</span>
                  </div>

                  <div className="property-footer">
                    <div className="property-price">
                      ₹ {property.price?.toLocaleString() || "Contact"}
                    </div>
                    <button
                      onClick={() => handleViewDetails(property.id)}
                      className="view-details-btn"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Simple Pagination Controls - Like in your image */}
        {properties.length > itemsPerPage && (
          <div className="simple-pagination">
            <button
              className="prev-btn"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left"></i> Prev
            </button>

            <div className="current-page">{currentPage}</div>

            <button
              className="next-btn"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}

        {properties.length === 0 && !loading && !category && (
          <p className="text-center">No properties available.</p>
        )}
      </div>
    </section>
  );
};

export default Properties;
